/**
 * Defiant Yarn — "x away from free shipping" progress bar.
 *
 * WHY THE CURRENCY MATH MATTERS
 *   Thresholds are entered in the SHOP's currency, because that is how Shopify
 *   evaluates a shipping rate's price condition. cart.total_price, though, is in
 *   the PRESENTMENT currency the shopper is seeing. Comparing the two raw numbers
 *   is the bug that marked a CAD 160 cart as qualifying for a USD 160 rate.
 *
 *   Shopify.currency.rate is the shop -> presentment rate it uses itself, so the
 *   threshold is converted with the same number before anything is compared.
 *
 * WHY THE MESSAGE IS SEEDED, NOT RANDOM PER PAINT
 *   paint() runs on every cart change (drawer qty, remove, pre-order add). Picking
 *   a new random line each time would make the copy flicker mid-browse. Instead a
 *   single random seed is drawn once per browser session and reused, so every bar
 *   on the page — drawer and cart page both — shows the same line, and it only
 *   changes on a fresh session.
 */
import { StandardEvents } from '@shopify/events';

const SELECTOR = '[data-df-fsb]';
const SEED_KEY = 'df-fsb-seed';

function currencyInfo() {
  const c = (window.Shopify && window.Shopify.currency) || {};
  const rate = parseFloat(c.rate);
  return {
    active: c.active || (window.Shopify && window.Shopify.currency_active) || 'USD',
    rate: Number.isFinite(rate) && rate > 0 ? rate : 1,
  };
}

function money(cents, code) {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: Math.round(cents) % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (err) {
    return '$' + amount.toFixed(2);
  }
}

/** One random float in [0, 1), stable for the browser session. */
function sessionSeed() {
  try {
    const stored = sessionStorage.getItem(SEED_KEY);
    if (stored !== null) return parseFloat(stored);
    const fresh = String(Math.random());
    sessionStorage.setItem(SEED_KEY, fresh);
    return parseFloat(fresh);
  } catch (err) {
    // Storage unavailable (privacy mode, etc.) — fall back to a seed that at
    // least stays consistent for the life of this page load.
    return Math.random();
  }
}

const SEED = sessionSeed();

/** attr is a JSON-encoded array of message lines (falls back to a plain string). */
function pickMessage(attr) {
  let lines;
  try {
    const parsed = JSON.parse(attr || '[]');
    lines = Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch (err) {
    lines = [attr || ''];
  }
  lines = lines.map((l) => (l || '').trim()).filter(Boolean);
  if (!lines.length) return '';
  return lines[Math.floor(SEED * lines.length) % lines.length];
}

/** @param {Element} bar @param {number} totalCents presentment cents */
function paint(bar, totalCents) {
  const shopThreshold = parseInt(bar.getAttribute('data-threshold'), 10);
  if (!Number.isFinite(shopThreshold) || shopThreshold <= 0) {
    bar.hidden = true;
    return;
  }

  const { active, rate } = currencyInfo();
  const threshold = Math.round(shopThreshold * rate);
  const total = Math.max(0, Math.round(totalCents));
  const remaining = threshold - total;
  const pct = Math.max(0, Math.min(100, (total / threshold) * 100));

  const fill = bar.querySelector('[data-df-fsb-fill]');
  const say = bar.querySelector('[data-df-fsb-say]');
  const done = remaining <= 0;

  bar.hidden = false;
  bar.setAttribute('data-df-fsb-state', done ? 'done' : 'going');
  bar.setAttribute('aria-valuenow', String(Math.round(pct)));

  if (fill) fill.style.width = pct.toFixed(2) + '%';

  if (say) {
    const template = pickMessage(bar.getAttribute(done ? 'data-say-done' : 'data-say-going'));
    say.textContent = template.replace(/%amount%/g, money(remaining, active));
  }
}

function paintAll(totalCents) {
  document.querySelectorAll(SELECTOR).forEach((bar) => paint(bar, totalCents));
}

/** First paint runs off the number Liquid rendered, so there is no flash and no fetch. */
function paintFromMarkup() {
  document.querySelectorAll(SELECTOR).forEach((bar) => {
    const total = parseInt(bar.getAttribute('data-total'), 10);
    paint(bar, Number.isFinite(total) ? total : 0);
  });
}

let pending = null;
function refresh() {
  if (pending) return pending;
  pending = fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_url ? window.Theme.routes.cart_url : '/cart') + '.js', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => response.json())
    .then((cart) => {
      paintAll(cart.total_price);
      return cart;
    })
    .catch(() => {})
    .then((cart) => {
      pending = null;
      return cart;
    });
  return pending;
}

paintFromMarkup();

// Every cart change in the theme lands here: drawer quantity, remove, and adds
// from the pre-order form. The drawer's own copy of the bar is re-rendered by the
// section morph, so re-reading the markup afterwards keeps them in step.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const settle = () => window.setTimeout(refresh, 0);
  if (event.promise && typeof event.promise.then === 'function') {
    event.promise.then(settle).catch(settle);
  } else {
    settle();
  }
});

document.addEventListener('shopify:section:load', paintFromMarkup);